import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { memo, useCallback, useEffect, useRef } from 'react'
import {
    setChatMinimized,
    setFullWindow,
} from '../../../../../../actions/layout'
import { sendPrivateMessage } from '../../../../../../actions/sendPrivateMessage'
import { sendRoomMessage } from '../../../../../../actions/sendRoomMessage'
import {
    closeDrawer,
    openWhiteboardDrawer,
} from '../../../../../../app-state/modals/state.drawer'
import { toggleFloaterVideo } from '../../../../../../app-state/modals/state.floater'
import {
    AppStateType,
    RoomView,
    VideoType,
} from '../../../../../../constants/enums'
import { isLevelModel } from '../../../../../../mfcLogic/userProperties/level'
import {
    VIDEOSTATE,
    enableRoundedVideo,
} from '../../../../../../state/state.video'
import { closeWhiteboard } from '../../../../../../state/state.whiteboard'
import { calcWidth } from '../../../../../../utilities/dimensions'
import i18n from '../../../../../../utilities/i18n'
import ButtonChatShow from '../../../../../Buttons/ButtonChatShow'
import { useAppStateSelector } from '../../../../../Hooks/useAppState'
import { useLayoutSelector } from '../../../../../Hooks/useLayout'
import { usePortalStateSelector } from '../../../../../Hooks/usePortalState'
import { useRoomSelector } from '../../../../../Hooks/useRoom'
import { useUserSelector } from '../../../../../Hooks/useUser'
import { useWhiteboardSelector } from '../../../../../Hooks/useWhiteboard'
import FlexGrid from '../../../../../common/FlexGrid'
import { palette } from '../../../../../styles/colors'
import { PAPER_HEIGHTS } from '../../../../../theme'
import Video from '../../../../Video'
import ChatActionsBar from '../../../ActionsBar'
import Compose from '../../../Compose'
import TokenCount from '../../../common/TokenCount'
import { calcVideoDimensions } from '../../../common/utilities'
import Chat from '../../Chat'
import VideoPoster from '../../VideoPoster'
import ViewerCount from '../../ViewerCount'
import {
    ViewProps,
    getLeftStyles,
    getRightStyles,
    resetZoomedVideoIcons,
    restoreChat,
} from '../common/helpers'
import {
    chatPaneStyles,
    fullWindowRootStyles,
    fullWindowStreamContainerStyles,
    gridRootStyles,
    videoGridStyles,
} from '../common/styles'

const FullWindowVideoSpacer = styled('div')({
    width: '100%',
    background: palette.videoBackground,
})

const chatGridStylesFullWindow = css({
    position: 'absolute',
    left: `calc(env(safe-area-inset-left, 0px) * 0.7)`,
    height: '100%', //'calc(50% + 40px)',
    width: '37% !important', // latest mui grid has some size based media queries that effect the width/flex basis
    maxWidth: '400px !important',
    flexBasis: '37% !important',
    marginBottom: 0,
    overflowX: 'hidden',
    overflowY: 'scroll',
    zIndex: PAPER_HEIGHTS.floatingChat,
    '&::-webkit-scrollbar': {
        display: 'none',
    },
})

const FullWindow = (props: ViewProps) => {
    const {
        id,
        size,
        roomView,
        height,
        width,
        fullWindow,
        disableKeyboardOpenDetection,
        viewingTips,
        toggleTips,
    } = props

    const chatGridContainerRef = useRef<HTMLDivElement>(null)

    const isLounge = useRoomSelector(id, 'isLounge')
    const loungeSurrogateID = useRoomSelector(id, 'loungeSurrogateID')
    const isWhiteboardOpen = useWhiteboardSelector('open')
    const composeFocused = usePortalStateSelector('composeFocused')

    const authenticated = useAppStateSelector(AppStateType.APP, 'authenticated')
    const tokens = useUserSelector('me', 'tokens')
    const level = useUserSelector('me', 'level')

    const surrogateName = useUserSelector(loungeSurrogateID, 'name')
    const isModel = useUserSelector(id, 'isModel')
    const isBroadcasting = useUserSelector(id, 'isBroadcasting')
    const isAway = useUserSelector(id, 'isAway')
    const name = useUserSelector(id, 'name')

    const isPM = roomView === RoomView.PM
    const isProfile = roomView === RoomView.Profile

    const modelName = !isLounge ? name : surrogateName
    const modelID = !isLounge ? id : loungeSurrogateID

    const showVideo = isPM || isProfile ? props.showVideo : true
    const noVideoDefault = !isBroadcasting || isAway

    const minimized = useLayoutSelector('minimized')

    const { height: videoHeight = 0, width: videoWidth = 0 } =
        calcVideoDimensions(width, height, false, true)

    const showTokens = authenticated && !isLevelModel(level)

    const leftStyles = getLeftStyles({
        isLounge,
        fullWindow,
        isModel,
    })

    const rightStyles = getRightStyles({
        fullWindow,
        isModel,
    })

    const calcVideoWidth = videoWidth
    const calcVideoHeight = videoHeight
    const isPillarBoxed = calcVideoHeight >= height || calcVideoWidth < width
    const verticalActionBar =
        fullWindow && width && height && width > calcWidth(height)
    // full window controls hack
    const onFullWindowShowControls = useCallback(
        (event) => {
            // this is some real nonsense, but it's a caveat of z-index and a cousin video element
            VIDEOSTATE[modelID]?.instance?.player?.userActive(true)
            const fullStack = document.elementsFromPoint(
                event.clientX,
                event.clientY,
            )
            const foundEndIndex = fullStack.findIndex(
                (el) => el.id === 'transition-cover',
            )
            const foundStartIndex = fullStack.findIndex(
                (el) => el.getAttribute('data-testid') === 'Container__Floater',
            )
            // console.log('[FloatingPortal.onFullWindowShowControls]', { fullStack })
            const stack = fullStack.slice(
                foundStartIndex > 0 ? foundStartIndex + 1 : 0,
                foundEndIndex ?? fullStack.length,
            )

            // eslint-disable-next-line fp/no-let
            let foundClick = false
            stack.forEach((el: HTMLElement) => {
                if (!foundClick && typeof el.click === 'function') {
                    el.click()
                    foundClick = true
                }
            })
        },
        [modelID],
    )

    const onToggleWhiteboard = useCallback(() => {
        if (isWhiteboardOpen) {
            closeDrawer(true)
            closeWhiteboard()
        } else {
            openWhiteboardDrawer(modelName)
        }
    }, [isWhiteboardOpen, modelName])
    const onToggleStream = useCallback(() => {
        if (!isBroadcasting) {
            setFullWindow(false)
        }

        toggleFloaterVideo()
    }, [isBroadcasting])

    useEffect(() => {
        if (!composeFocused && chatGridContainerRef.current) {
            chatGridContainerRef.current.scrollTop = 0
        }
    }, [composeFocused])

    // handle video rounding css
    useEffect(() => {
        resetZoomedVideoIcons()
        enableRoundedVideo(modelID, false, false, size, false)
    }, [modelID, size])

    // console.log('[FloatingPortal.fullWindow]', {
    //     isPillarBoxed,
    //     isLetterBoxed,
    //     height,
    //     calcVideoHeight,
    //     width,
    //     calcVideoWidth,
    // })

    const ActionsBar = (
        <ChatActionsBar
            id={id}
            floating
            fullWindow
            verticalLayout={verticalActionBar}
            roomView={roomView}
            video={!noVideoDefault ? showVideo : undefined}
            onWhiteboard={onToggleWhiteboard}
            onVideoToggle={!noVideoDefault ? onToggleStream : undefined}
        />
    )
    const ChatInputBar = (
        <Compose
            hide={minimized}
            id={id}
            onSend={isPM ? sendPrivateMessage : sendRoomMessage}
            setMinimized={setChatMinimized}
            placeholder={i18n.t(
                isPM ? 'chat.sendPrivateMessage' : 'chat.sendChatMessage',
            )}
            hideTipButton={!isModel}
            disableKeyboardOpenDetection={disableKeyboardOpenDetection}
            pm={isPM}
        />
    )

    return (
        <div css={fullWindowRootStyles}>
            <FlexGrid overrides={gridRootStyles} direction="row" wrap="wrap">
                {/* [Video Section] we're in a model's room or one of the lounges */}
                {isModel && (
                    <FlexGrid
                        overrides={videoGridStyles}
                        style={{
                            maxWidth: leftStyles === 'auto' ? null : leftStyles,
                            flexBasis: leftStyles,
                            flexFlow: isPillarBoxed
                                ? 'row nowrap'
                                : 'column nowrap',
                        }}
                        // xs={6}
                        data-testid="Video__Section"
                        // item
                    >
                        <FullWindowVideoSpacer
                            style={{
                                height: isPillarBoxed
                                    ? height
                                    : `${
                                          Math.floor(
                                              (height - calcVideoHeight) / 2,
                                          ) + 1
                                      }px`,
                                width: isPillarBoxed
                                    ? `${Math.floor(
                                          (width - calcVideoWidth) / 2,
                                      )}px`
                                    : width,
                            }}
                        />
                        {/* in a model's room and allowed to view her stream or in the lounge */}
                        <div
                            css={fullWindowStreamContainerStyles}
                            style={{
                                height: calcVideoHeight,
                                maxHeight: calcVideoHeight,
                                width: calcVideoWidth,
                                maxWidth: calcVideoWidth,
                            }}
                            onClick={onFullWindowShowControls}
                        >
                            {/* hide video if in the lounge with no model selected */}
                            {isLounge && !loungeSurrogateID && <VideoPoster />}
                            {(!isLounge || !!loungeSurrogateID) && (
                                <Video
                                    id={modelID}
                                    extendedPreviewLabel
                                    videoType={VideoType.Room}
                                />
                            )}
                        </div>
                        <FullWindowVideoSpacer
                            style={{
                                height: isPillarBoxed
                                    ? height
                                    : `${
                                          Math.floor(
                                              (height - calcVideoHeight) / 2,
                                          ) + 1
                                      }px`,
                                width: isPillarBoxed
                                    ? `${
                                          Math.floor(
                                              (width - calcVideoWidth) / 2,
                                          ) + 1
                                      }px`
                                    : width,
                            }}
                        />
                    </FlexGrid>
                )}
                {/* [Chat Section] if we're allowed in */}
                <FlexGrid
                    ref={chatGridContainerRef}
                    overrides={chatGridStylesFullWindow}
                    style={{
                        flexBasis: rightStyles,
                        maxWidth: rightStyles,
                        overflowX: minimized ? 'visible' : 'hidden',
                        overflowY: minimized
                            ? 'visible'
                            : isWhiteboardOpen
                              ? 'scroll'
                              : 'hidden',
                    }}
                    data-testid="Chat__Section"
                >
                    {/* [portrait] chat actions bar is below the video */}
                    <div css={chatPaneStyles} data-testid="Chat__Container">
                        <Chat
                            key={`chat-${id}`}
                            id={id}
                            height={height}
                            showingTips={viewingTips}
                            onTipToggle={toggleTips}
                            onOpenWhiteboard={onToggleWhiteboard}
                            styleDisplay={minimized ? 'none' : undefined}
                            styleHeight={'100%'}
                            pm={isPM}
                        />
                        {ChatInputBar}
                    </div>
                </FlexGrid>
                {/* [full-window] chat actions bar is in the top-right corner (desktop) or right side (mobile) */}
                {!minimized && isModel && (
                    <>
                        {ActionsBar}
                        {showTokens && <TokenCount tokens={tokens} />}
                        {!isPM && (
                            <ViewerCount
                                id={id}
                                fullWindow
                                {...(showTokens
                                    ? { fullWindowPushDown: 24 }
                                    : {})}
                            />
                        )}
                    </>
                )}
                {/* [full-window] button to reveal floating chat panel */}
                {!!minimized && <ButtonChatShow onClick={restoreChat} />}
            </FlexGrid>
        </div>
    )
}

export default memo(FullWindow)
